import type { ApiResponse, PaginationResponse } from "@/interface/common";

/**
 * Formats a date string, Date object, or timestamp according to the specified format pattern.
 * Default format pattern: "DD-MM-YYYY"
 *
 * Supported Tokens:
 *   YYYY - 4-digit year (e.g. 2026)
 *   YY   - 2-digit year (e.g. 26)
 *   MMMM - Full month name (e.g. January)
 *   MMM  - Short month name (e.g. Jan)
 *   MM   - 2-digit month (01-12)
 *   DD   - 2-digit day of month (01-31)
 */
export function formatDate(
  dateInput?: string | Date | null,
  formatPattern: string = "DD-MM-YYYY",
): string {
  if (!dateInput) return "";

  if (
    typeof dateInput === "string" &&
    dateInput.trim().toLowerCase() === "present"
  ) {
    return dateInput;
  }

  let date: Date;

  if (typeof dateInput === "string") {
    const cleanDateStr = dateInput.trim();
    // Parse YYYY-MM-DD or YYYY-MM without UTC timezone offset shift
    const isoDateMatch = cleanDateStr.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
    if (isoDateMatch) {
      const year = parseInt(isoDateMatch[1], 10);
      const month = parseInt(isoDateMatch[2], 10) - 1;
      const day = isoDateMatch[3] ? parseInt(isoDateMatch[3], 10) : 1;
      date = new Date(year, month, day);
    } else {
      date = new Date(cleanDateStr);
    }
  } else {
    date = dateInput;
  }

  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return typeof dateInput === "string" ? dateInput : "";
  }

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const monthNamesShort = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthNamesFull = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

  return formatPattern
    .replace(/\bYYYY\b/g, String(year))
    .replace(/\bYY\b/g, String(year).slice(-2))
    .replace(/\bMMMM\b/g, monthNamesFull[date.getMonth()])
    .replace(/\bMMM\b/g, monthNamesShort[date.getMonth()])
    .replace(/\bMM\b/g, pad(month))
    .replace(/\bDD\b/g, pad(day));
}

/**
 * Helper to check if both page and page_size exist in request parameters.
 */
export const isPaginated = (params?: { page?: number; page_size?: number }) =>
  Boolean(params?.page && params?.page_size);

/**
 * Helper to calculate the next page number for useInfiniteQuery.
 * Handles both paginated responses (ApiResponse<PaginationResponse<T>>)
 * and unpaginated responses (ApiResponse<T[]>).
 */
export const getNextPage = <T = any>(
  lastPage: ApiResponse<PaginationResponse<T> | T[]>,
  allPages: ApiResponse<PaginationResponse<T> | T[]>[],
  pageSize: number = 10,
): number | undefined => {
  const dataObj = lastPage?.data;

  // If response is a direct array (unpaginated ApiResponse<T[]>):
  if (Array.isArray(dataObj)) {
    return undefined;
  }

  const results = dataObj?.results;
  if (!results || results.length === 0) return undefined;

  const next = dataObj?.next;
  if (typeof next === "number") return next;

  const totalPages = dataObj?.totalPages;
  const currentPage = allPages.length;
  if (totalPages && currentPage < totalPages) return currentPage + 1;

  if (results.length >= pageSize) return currentPage + 1;
  return undefined;
};

/**
 * Generates initials by splitting a name by space and getting the first character of the first two parts.
 * Example: "John Doe" -> "JD", "Bhumit Patel" -> "BP", "Bhumit" -> "B", "" -> "CA"
 */
export function getInitials(
  name?: string | null,
  fallback: string = "CA",
): string {
  if (!name || !name.trim()) return fallback;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
}

/**
 * Returns a greeting message based on current time of day.
 * - 04:00 to 11:59: "Good Morning"
 * - 12:00 to 16:59: "Good Afternoon"
 * - 17:00 to 03:59: "Good Evening"
 */
export function getGreeting(date: Date = new Date()): string {
  const hour = date.getHours();
  if (hour >= 4 && hour < 12) {
    return "Good Morning";
  }
  if (hour >= 12 && hour < 17) {
    return "Good Afternoon";
  }
  return "Good Evening";
}

